import React, { useEffect, useState } from 'react';
import { Pause, Play } from 'lucide-react';

// Owner-confirmed organisations. Logos from each organisation's own website; tile matches the logo's intended background.
export const ORGANISATIONS = [
  { name: 'British Heart Foundation', logo: '/logos/bhf-logo.svg', w: 124, h: 35, tile: 'bg-white' },
  { name: 'The Salvation Army', logo: '/logos/salvation-army.svg', w: 210, h: 240, tile: 'bg-white' },
  { name: 'YMCA', logo: '/logos/ymca.png', w: 970, h: 360, tile: 'bg-white' },
  { name: 'Inside Success', logo: null, tile: 'bg-white' },
  { name: 'Redbridge Council', logo: '/logos/redbridge.png', w: 166, h: 70, tile: 'bg-[color:var(--mv-navy)]' },
  { name: 'Sheffield City Council', logo: '/logos/sheffield.svg', w: 1461, h: 1146, tile: 'bg-[color:var(--mv-navy)]' },
];

const useReducedMotion = () => {
  const [r, setR] = useState(() => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    const m = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!m) return undefined;
    const fn = (e) => setR(e.matches);
    m.addEventListener('change', fn);
    return () => m.removeEventListener('change', fn);
  }, []);
  return r;
};
export { useReducedMotion };

const Tile = ({ o, hidden }) => (
  <li className={`shrink-0 h-16 sm:h-20 w-36 sm:w-52 rounded-xl border border-slate-200 ${o.tile} flex items-center justify-center px-5`} aria-hidden={hidden || undefined} data-testid={hidden ? undefined : `org-${o.name.toLowerCase().replace(/\s+/g, '-')}`}>
    {o.logo
      ? <img src={o.logo} alt={hidden ? '' : `${o.name} logo`} width={o.w} height={o.h} loading="lazy" className="max-h-12 max-w-full w-auto h-auto object-contain" />
      : <span className="font-display font-extrabold text-[color:var(--mv-navy)] text-lg text-center leading-tight">{o.name}</span>}
  </li>
);

export const OrgCarousel = () => {
  const reduced = useReducedMotion();
  const [paused, setPaused] = useState(false);
  const moving = !reduced && !paused;
  return (
    <section className="py-6 md:py-10 bg-white border-b border-slate-100" aria-labelledby="orgs-title" data-testid="org-carousel">
      <div className="container-xl flex items-center justify-between gap-3">
        <h2 id="orgs-title" className="text-xs font-bold tracking-[0.2em] uppercase text-slate-600">Organisations we’ve worked with</h2>
        {!reduced && (
          <button type="button" onClick={() => setPaused(p => !p)} className="h-10 w-10 rounded-full border border-slate-300 bg-white inline-flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--mv-orange)]" aria-label={paused ? 'Play logo animation' : 'Pause logo animation'} data-testid="org-carousel-toggle">
            {paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
          </button>
        )}
      </div>
      {reduced ? (
        <ul className="container-xl mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">{ORGANISATIONS.map(o => <Tile key={o.name} o={o} />)}</ul>
      ) : (
        <div className="mt-4 overflow-hidden mv-marquee-mask" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)} tabIndex={0} aria-label="Organisation logos, scrolling" role="region">
          <ul className={`flex gap-4 w-max mv-marquee ${moving ? '' : 'mv-marquee-paused'}`} data-testid="org-marquee">
            {ORGANISATIONS.map(o => <Tile key={o.name} o={o} />)}
            {ORGANISATIONS.map(o => <Tile key={`dup-${o.name}`} o={o} hidden />)}
          </ul>
        </div>
      )}
    </section>
  );
};
